import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api';
import { useLanguage } from '../../shell/language-context';
import { useResource } from '../../shell/use-resource';
import { compactCount } from '../reels/reel-counts';
import { reelEmbed } from '../reels/reel-embed';
import { ReelViewer, type ReelCard } from '../reels/reel-viewer';
import { Ic } from './hx-icon';

// "Offer reels" (design `.sec.reels`, FR-C29): the newest approved reels across live
// listings (GET /v1/reels, API-243) as 9:16 tiles — YouTube's own still, the play count, the
// business and the caption — and every tile opens the fullscreen viewer on its reel. The
// design's duration chip is not drawn: only the video id is stored, never a length. Hidden
// until there is at least one reel, so an empty platform shows no empty band. The viewer
// renders itself on <body> (reel-viewer.tsx), clear of the design's `.rv` reveal class.
function Tile({ reel, onOpen }: { reel: ReelCard; onOpen: (el: HTMLElement) => void }) {
  const { t, lang } = useLanguage();
  const [noPoster, setNoPoster] = useState(false);
  const embed = reelEmbed(reel.video_id);
  const views = reel.counts.views > 0 ? compactCount(lang, reel.counts.views, t) : '';
  return (
    <button type="button" className="reel rv" onClick={(e) => onOpen(e.currentTarget)} aria-label={t('reels.open', { name: reel.caption ?? reel.business.name })}>
      {embed && !noPoster ? <img src={embed.poster} alt="" loading="lazy" decoding="async" onError={() => setNoPoster(true)} /> : null}
      {views ? (
        <span className="rv2">
          <Ic id="video" />
          {views}
        </span>
      ) : null}
      <span className="play" aria-hidden="true" />
      <div className="rtx">
        <b>{reel.business.name}</b>
        <small>
          <Ic id="pct" />
          <span className="cap">{reel.caption ?? ''}</span>
        </small>
      </div>
    </button>
  );
}

export function HxReels() {
  const { t, lang } = useLanguage();
  const reels = useResource<{ items: ReelCard[] }>((signal) => api.GET('/reels', { signal, lang }), [lang]);
  const [open, setOpen] = useState<number | null>(null);
  const lastTile = useRef<HTMLElement | null>(null);
  const items = reels.data?.items ?? [];
  if (!items.length) return null;
  return (
    <section className="sec reels" aria-labelledby="hx-reels-h">
      <div className="in">
        <div className="sh rv">
          <div>
            <h2 id="hx-reels-h">
              {t('hx.reels.title')} <span className="bdg new">{t('hx.new')}</span>
            </h2>
            <p>{t('hx.reels.sub')}</p>
          </div>
          <Link className="more" to="/videos">
            {t('hx.reels.all')} <Ic id="arrow" />
          </Link>
        </div>
        <div className="hs rl">
          {items.map((r, i) => (
            <Tile
              key={r.id}
              reel={r}
              onOpen={(el) => {
                lastTile.current = el;
                setOpen(i);
              }}
            />
          ))}
        </div>
      </div>
      {open !== null ? <ReelViewer reels={items} start={open} onClose={() => setOpen(null)} returnFocus={lastTile.current} /> : null}
    </section>
  );
}
